import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import {
  Box,
  Typography,
  CircularProgress,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  ToggleButton,
  ToggleButtonGroup,
  Paper,
  Checkbox,
  FormControlLabel,
} from "@mui/material";
import {
  CloudUpload,
  GridView,
  ViewList,
  Download,
  Delete,
  Refresh,
} from "@mui/icons-material";
import { useDispatch, useSelector, shallowEqual } from "react-redux";
import { RootState } from "@/setup/redux/RootReducer";
import {
  fetchOrderDocuments,
  uploadOrderDocumentAsync,
  deleteDocumentAsync,
  renameDocumentAsync,
  clearDocuments,
  clearError,
  fetchDocumentTypes,
  getDownloadUrlAsync,
  Document,
} from "../redux";
import DocumentFilters from "./DocumentFilters";
import DocumentList from "./DocumentList";
import FileUploadModal from "./FileUploadModal";
import UniversalFileViewer from "./UniversalFileViewer";
import axiosInstance from "@/setup/axios/axiosInstance";
import { startLoading, stopLoading } from "@/__core/redux/globalLoadingSlice";
import MessageSnackbar from "@/__core/helpers/components/MessageSnackbar";

// Custom hook to manage filter state with stable references
const useFilterState = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedDocumentTypes, setSelectedDocumentTypes] = useState<string[]>([]);
  const [selectedUploadedBy, setSelectedUploadedBy] = useState<string[]>([]);
  const [dateRange, setDateRange] = useState<{start: string, end: string}>({start: '', end: ''});

  const handleDocumentTypeChange = useCallback((typeId: string) => {
    setSelectedDocumentTypes(prev => {
      const newSelection = prev.includes(typeId) 
        ? prev.filter(id => id !== typeId)
        : [...prev, typeId];
      
      if (newSelection.length !== prev.length || !newSelection.every(id => prev.includes(id))) {
        return newSelection;
      }
      return prev;
    });
  }, []);

  const handleUploadedByChange = useCallback((uploaderId: string) => {
    setSelectedUploadedBy(prev => {
      const newSelection = prev.includes(uploaderId) 
        ? prev.filter(id => id !== uploaderId)
        : [...prev, uploaderId];
      
      if (newSelection.length !== prev.length || !newSelection.every(id => prev.includes(id))) {
        return newSelection;
      }
      return prev;
    });
  }, []);

  const handleSearchChange = useCallback((value: string) => {
    setSearchTerm(value);
  }, []);

  const handleDateRangeChange = useCallback((range: { start: string; end: string }) => {
    setDateRange(range);
  }, []);

  return {
    searchTerm,
    selectedDocumentTypes,
    selectedUploadedBy,
    dateRange,
    handleDocumentTypeChange,
    handleUploadedByChange,
    handleSearchChange,
    handleDateRangeChange,
    setSearchTerm,
    setSelectedDocumentTypes,
    setSelectedUploadedBy,
    setDateRange
  };
};

// Custom hook for Redux state with shallow comparison
const useDocumentState = () => {
  const documents = useSelector((state: RootState) => state.documents.documents, shallowEqual);
  const documentTypes = useSelector((state: RootState) => state.documents.documentTypes, shallowEqual);
  const loading = useSelector((state: RootState) => state.documents.loading);
  const error = useSelector((state: RootState) => state.documents.error);
  const uploadProgress = useSelector((state: RootState) => state.documents.uploadProgress);
  const authState = useSelector((state: RootState) => state.auth, shallowEqual);
  
  return {
    documents,
    documentTypes,
    loading,
    error,
    uploadProgress,
    authState
  };
};



// Memoized DocumentList Component with custom comparison
const MemoizedDocumentList = React.memo(DocumentList, (prevProps, nextProps) => {
  // Only re-render if documents, viewMode, selectedFiles, or authState actually changed
  return (
    prevProps.documents === nextProps.documents &&
    prevProps.viewMode === nextProps.viewMode &&
    prevProps.selectedFiles === nextProps.selectedFiles &&
    prevProps.authState === nextProps.authState &&
    prevProps.onFileSelect === nextProps.onFileSelect &&
    prevProps.onFileDownload === nextProps.onFileDownload &&
    prevProps.onFilePreview === nextProps.onFilePreview
  );
});



interface DocumentManagementProps {
  orderId?: string;
}

// Separate component for main content to prevent re-renders
const DocumentManagementContent = React.memo(({ 
  documents, 
  viewMode, 
  selectedFiles, 
  onFileSelect, 
  onFileDownload, 
  onFilePreview, 
  authState, 
  onSelectAll, 
  onDeleteSelected, 
  onUploadClick,
  loading = false
}: any) => {
  
  return (
    <Paper sx={{ flex: 1, p: 2 }}>
      {/* File Actions Bar */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={selectedFiles.length === documents.length && documents.length > 0}
                indeterminate={selectedFiles.length > 0 && selectedFiles.length < documents.length}
                onChange={onSelectAll}
              />
            }
            label={`Chọn tất cả (${documents.length})`}
          />
          {selectedFiles.length > 0 && (
            <Box
              sx={{
                px: 1.5,
                py: 0.5,
                borderRadius: 1,
                bgcolor: 'primary.main',
                color: 'primary.contrastText',
                fontSize: '0.75rem',
                fontWeight: 500
              }}
            >
              Đã chọn: {selectedFiles.length}
            </Box>
          )}
        </Box>
        
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            size="small"
            startIcon={<Download />}
            disabled={selectedFiles.length === 0}
            onClick={() => {
              selectedFiles.forEach((fileId: string) => onFileDownload(fileId));
            }}
          >
            Tải xuống
          </Button>
          <Button
            size="small"
            startIcon={<Delete />}
            color="error"
            disabled={selectedFiles.length === 0 || !authState.accessToken}
            onClick={onDeleteSelected}
          >
            Xóa
          </Button>
        </Box>
      </Box>

      {/* File Grid/List */}
      <MemoizedDocumentList
        documents={documents}
        viewMode={viewMode}
        selectedFiles={selectedFiles}
        onFileSelect={onFileSelect}
        onFileDownload={onFileDownload}
        onFilePreview={onFilePreview}
        authState={authState}
        loading={loading}
      />
    </Paper>
  );
});

// Completely isolated main component
const IsolatedDocumentManagement = React.memo(({ orderId }: DocumentManagementProps) => {
  const dispatch = useDispatch();
  // Use custom hooks for stable state
  const { documents, documentTypes, loading, error, uploadProgress, authState } = useDocumentState();
  
  // Use custom hook for filter state
  const {
    searchTerm,
    selectedDocumentTypes,
    selectedUploadedBy,
    dateRange,
    handleDocumentTypeChange,
    handleUploadedByChange,
    handleSearchChange,
    handleDateRangeChange,
    setSearchTerm,
    setSelectedDocumentTypes,
    setSelectedUploadedBy,
    setDateRange
  } = useFilterState();

  // Other state management
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [selectedFiles, setSelectedFiles] = useState<string[]>([]);
  const [showDeleteConfirmDialog, setShowDeleteConfirmDialog] = useState(false);
  const [showFileUploadDialog, setShowFileUploadDialog] = useState(false);
  const [previewDocument, setPreviewDocument] = useState<{id: string, name: string} | null>(null);
  
  // MessageSnackbar state
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState<string | null>(null);
  const [snackbarSeverity, setSnackbarSeverity] = useState<'error' | 'warning' | 'info' | 'success'>('error');
  
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Function to show snackbar
  const showSnackbar = useCallback((message: string, severity: 'error' | 'warning' | 'info' | 'success' = 'error') => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  }, []);

  // Function to close snackbar
  const handleCloseSnackbar = useCallback(() => {
    setSnackbarOpen(false);
    setSnackbarMessage(null);
  }, []);

  // Show error snackbar when error state changes
  useEffect(() => {
    if (error) {
      const errorMessage = error.includes('401') || error.includes('Unauthorized') 
        ? 'Lỗi xác thực: Vui lòng đăng nhập lại để tiếp tục tải lên tài liệu.'
        : error;
      showSnackbar(errorMessage, 'error');
    }
  }, [error, showSnackbar]);

  // Load documents when orderId changes
  useEffect(() => {
    if (orderId) {
      // Clear all state first
      setSelectedFiles([]);
      setSearchTerm('');
      setSelectedDocumentTypes([]);
      setSelectedUploadedBy([]);
      setDateRange({start: '', end: ''});
        // Clear documents from Redux store
        dispatch(clearDocuments());
        // Fetch fresh documents
        dispatch(fetchOrderDocuments({ orderId }));
    } else {
      dispatch(clearDocuments());
      setSelectedFiles([]);
    }
  }, [orderId, dispatch]);

  // Load document types on component mount
  useEffect(() => {
    // Only fetch if user is authenticated and documentTypes is empty
    if (authState.accessToken && documentTypes.length === 0) {
      dispatch(fetchDocumentTypes());
    }
  }, [authState.accessToken, documentTypes.length, dispatch]);

  // Clear selected files when documents change to remove stale IDs
  useEffect(() => {
    if (documents.length > 0 && selectedFiles.length > 0) {
      // Filter out any selected files that don't exist in current documents
      const validSelectedFiles = selectedFiles.filter(fileId => 
        documents.some(doc => doc.id === fileId)
      );
      if (validSelectedFiles.length !== selectedFiles.length) {
        setSelectedFiles(validSelectedFiles);
      }
    }
  }, [documents]); // Remove selectedFiles from dependencies to prevent re-render loop


  // Use ref to store latest filter values to avoid dependency issues
  const filterRef = useRef({
    selectedDocumentTypes,
    selectedUploadedBy,
    dateRange,
    searchTerm,
    orderId,
    documentTypes
  });

  // Update ref when values change
  useEffect(() => {
    filterRef.current = {
      selectedDocumentTypes,
      selectedUploadedBy,
      dateRange,
      searchTerm,
      orderId,
      documentTypes
    };
  });

  // Stable filter application function
  const applyFiltersStable = useCallback(() => {
    const { selectedDocumentTypes, selectedUploadedBy, dateRange, searchTerm, orderId, documentTypes } = filterRef.current;
    
    if (!orderId) return;
    
    const filters: any = {};
    
    // Add document type filter
    if (selectedDocumentTypes.length > 0) {
      const documentType = documentTypes.find(dt => selectedDocumentTypes.includes(dt.id));
      if (documentType) {
        filters.documentType = documentType.name;
      }
    }
    
    // Add uploaded by filter
    if (selectedUploadedBy.length > 0) {
      filters.uploadedBy = selectedUploadedBy[0];
    }
    
    // Add date range filter
    if (dateRange.start) {
      filters.dateFrom = dateRange.start;
    }
    if (dateRange.end) {
      filters.dateTo = dateRange.end;
    }
    
    // Add search filter
    if (searchTerm) {
      filters.search = searchTerm;
    }
    
    // Only pass filters if there are any filters applied
    const hasFilters = Object.keys(filters).length > 0;
    dispatch(fetchOrderDocuments({ orderId, filters: hasFilters ? filters : undefined }));
  }, [dispatch]);

  // Apply filters when filter values change - debounced
  useEffect(() => {
    if (orderId) {
      const timeoutId = setTimeout(() => {
        applyFiltersStable();
      }, 500);
      
      return () => clearTimeout(timeoutId);
    }
  }, [selectedDocumentTypes, selectedUploadedBy, dateRange, searchTerm, orderId, documentTypes, applyFiltersStable]);

  // Event handlers
  const handleFileUpload = async (file: File) => {
    if (!orderId || !authState.accessToken) return;
    dispatch(clearError());
    dispatch(uploadOrderDocumentAsync({ orderId, file }));
  };

  const handleFileUploadSuccess = () => {
    // Refresh documents after successful upload
    if (orderId) {
      dispatch(fetchOrderDocuments({ orderId }));
      // Clear selected files to avoid stale IDs
      setSelectedFiles([]);
    }
  };

  const handleFileDelete = async (fileId: string) => {
    dispatch(clearError());
    const result = await dispatch(deleteDocumentAsync(fileId));
    
    // Refresh documents after successful deletion
    if (deleteDocumentAsync.fulfilled.match(result) && orderId) {
      dispatch(fetchOrderDocuments({ orderId }));
      // Remove deleted file from selected files
      setSelectedFiles(prev => prev.filter(id => id !== fileId));
    }
  };


  const handleFilePreview = useCallback((fileId: string) => {
    const document = documents.find(doc => doc.id === fileId);
    if (document) {
      setPreviewDocument({ id: fileId, name: document.name });
    }
  }, [documents]);

  const handleFileDownload = useCallback(async (fileId: string) => {
    try {
      dispatch(clearError());
      
      // Check if document exists in current documents list
      const documentExists = documents.some(doc => doc.id === fileId);
      if (!documentExists) {
        if (orderId) {
          // Clear all state
          setSelectedFiles([]);
          setSearchTerm('');
          setSelectedDocumentTypes([]);
          setSelectedUploadedBy([]);
          setDateRange({start: '', end: ''});
          // Clear documents from Redux store
          dispatch(clearDocuments());
          dispatch(fetchOrderDocuments({ orderId }));
        }
        alert('Tài liệu không tồn tại. Đang tải lại danh sách...');
        return;
      }
      
      // Show loading indicator
      dispatch(startLoading({ message: 'Đang tải xuống file...' }));
      
      // Use axiosInstance to download file with proper authentication
      const downloadUrl = `documents/${fileId}/download/`;
      
      try {
        const response = await axiosInstance.get(downloadUrl, {
          responseType: 'blob', // Important: tell axios to expect binary data
        });
        
        // Get the file blob
        const blob = response.data;
        const fileName = documents.find(doc => doc.id === fileId)?.name || 'download';
        
        // Create download link
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = fileName;
        link.style.display = 'none';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        
        // Clean up
        window.URL.revokeObjectURL(url);
        
      } catch (error) {
        alert('Không thể tải xuống file. Vui lòng thử lại sau.');
      } finally {
        // Hide loading indicator
        dispatch(stopLoading());
      }
    } catch (error) {
      alert('Lỗi khi tải xuống file. Vui lòng thử lại sau.');
      // Hide loading indicator on error
      dispatch(stopLoading());
    }
  }, [documents, orderId, dispatch]);


  const handleFileInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (files && files.length > 0) {
      handleFileUpload(files[0]);
    }
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };


  const handleFileSelect = useCallback((fileId: string) => {
    setSelectedFiles(prev => 
      prev.includes(fileId) 
        ? prev.filter(id => id !== fileId)
        : [...prev, fileId]
    );
  }, []);


  // Get unique uploaders from documents
  const uniqueUploaders = useMemo(() => {
    return Array.from(new Set(
      documents.map((doc: any) => doc.uploadedBy || doc.uploaded_by_name || doc.uploaded_by || 'Unknown')
    )).filter(Boolean);
  }, [documents]);

  // Since filtering is now done on the backend, we just need to exclude folders
  const filteredDocuments = useMemo(() => {
    return documents.filter((doc: any) => {
      // Skip folders
      if (doc.isDirectory) return false;
      return true;
    });
  }, [documents]);

  const handleSelectAllCallback = useCallback(() => {
    if (selectedFiles.length === filteredDocuments.length) {
      setSelectedFiles([]);
    } else {
      setSelectedFiles(filteredDocuments.map(doc => doc.id));
    }
  }, [selectedFiles.length, filteredDocuments]);

  const handleFileDownloadCallback = useCallback((fileId: string) => {
    handleFileDownload(fileId);
  }, [handleFileDownload]);

  const handleFilePreviewCallback = useCallback((fileId: string) => {
    handleFilePreview(fileId);
  }, [handleFilePreview]);





  // Show message when no orderId
  if (!orderId) {
    return (
      <Box sx={{ p: 3, textAlign: "center" }}>
        <Typography variant="h6" color="text.secondary" sx={{ mb: 2 }}>
          Quản lý tài liệu
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Lưu đơn hàng để có thể quản lý tài liệu
        </Typography>
      </Box>
    );
  }


  return (
    <Box sx={{ p: 0, display: 'flex', flexDirection: 'column' }}>
      {/* Show warning snackbar for unauthenticated users */}
      {!authState.accessToken && (
        <MessageSnackbar
          open={true}
          message="Cảnh báo: Bạn chưa đăng nhập. Vui lòng đăng nhập để có thể tải lên tài liệu."
          severity="warning"
          onClose={() => {}}
        />
      )}

      {uploadProgress > 0 && uploadProgress < 100 && (
        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" color="text.secondary">
            Đang tải lên: {uploadProgress}%
          </Typography>
        </Box>
      )}

      {/* Top Toolbar */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 2 }}>
          <Button
            variant="contained"
            startIcon={<CloudUpload />}
            onClick={() => setShowFileUploadDialog(true)}
            disabled={!authState.accessToken}
          >
            Upload File
          </Button>
          <ToggleButtonGroup
            value={viewMode}
            exclusive
            onChange={(_, newMode) => newMode && setViewMode(newMode)}
            size="small"
          >
            <ToggleButton value="grid">
              <GridView />
            </ToggleButton>
            <ToggleButton value="list">
              <ViewList />
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>
      </Paper>

      {/* Main Content */}
      <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
        {/* Left Sidebar - Filters */}
        <DocumentFilters
          searchTerm={searchTerm}
          onSearchChange={handleSearchChange}
          selectedDocumentTypes={selectedDocumentTypes}
          onDocumentTypeChange={handleDocumentTypeChange}
          selectedUploadedBy={selectedUploadedBy}
          onUploadedByChange={handleUploadedByChange}
          uniqueUploaders={uniqueUploaders}
          dateRange={dateRange}
          onDateRangeChange={handleDateRangeChange}
          documentTypes={documentTypes}
          loading={false}
        />

        {/* Right Content - File Display */}
        <DocumentManagementContent
              documents={filteredDocuments}
              viewMode={viewMode}
              selectedFiles={selectedFiles}
              onFileSelect={handleFileSelect}
          onFileDownload={handleFileDownloadCallback}
          onFilePreview={handleFilePreviewCallback}
              authState={authState}
          onSelectAll={handleSelectAllCallback}
          onDeleteSelected={() => setShowDeleteConfirmDialog(true)}
          onUploadClick={() => setShowFileUploadDialog(true)}
          loading={loading}
            />
      </Box>

      {/* Hidden file input */}
      <input
        type="file"
        ref={fileInputRef}
        onChange={handleFileInputChange}
        style={{ display: 'none' }}
        accept=".txt,.png,.jpg,.jpeg,.pdf,.doc,.docx,.zip,.rar,.mp4,.mp3,.avi,.mov,.gif,.bmp,.tiff,.svg,.webp"
      />


      {/* Delete Confirmation Dialog */}
      <Dialog
        open={showDeleteConfirmDialog}
        onClose={() => setShowDeleteConfirmDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Xác nhận xóa</DialogTitle>
        <DialogContent>
          <Typography>
            Bạn có chắc chắn muốn xóa {selectedFiles.length} file đã chọn không?
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Hành động này không thể hoàn tác.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setShowDeleteConfirmDialog(false)}
          >
            Hủy
          </Button>
          <Button
            onClick={() => {
              selectedFiles.forEach(fileId => handleFileDelete(fileId));
              setSelectedFiles([]);
              setShowDeleteConfirmDialog(false);
            }}
            variant="contained"
            color="error"
          >
            Xóa
          </Button>
        </DialogActions>
      </Dialog>

      {/* File Upload Dialog */}
      <FileUploadModal
        open={showFileUploadDialog}
        onClose={() => setShowFileUploadDialog(false)}
        onSuccess={handleFileUploadSuccess}
        orderId={orderId}
      />

      {/* Document Preview Modal */}
      <UniversalFileViewer
        open={!!previewDocument}
        onClose={() => setPreviewDocument(null)}
        documentId={previewDocument?.id || ''}
        documentName={previewDocument?.name || ''}
      />

      {/* MessageSnackbar for errors and notifications */}
      <MessageSnackbar
        open={snackbarOpen}
        message={snackbarMessage}
        severity={snackbarSeverity}
        onClose={handleCloseSnackbar}
      />

    </Box>
  );
});

IsolatedDocumentManagement.displayName = 'IsolatedDocumentManagement';

// Wrapper component to prevent re-renders
const DocumentManagement: React.FC<DocumentManagementProps> = React.memo(({ orderId }) => {
  return <IsolatedDocumentManagement orderId={orderId} />;
}, (prevProps, nextProps) => {
  return prevProps.orderId === nextProps.orderId;
});

DocumentManagement.displayName = 'DocumentManagement';

export default DocumentManagement;
